import axiosInstance from "../../services/axiosInstance";
import PieChartWithCustomizedLabel from "./pie_chart_custom_label";
import { useEffect, useState } from "react";
import { Box, Card, Grid, Paper, Typography, Skeleton } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { motion } from "framer-motion";

import {
  FiberNew,
  CheckCircleOutlineRounded,
  PriorityHighRounded,
} from "@mui/icons-material";

const OverviewPage = () => {
  // States
  const [stats, setStats] = useState<any>(null); // To store the API data
  const [loading, setLoading] = useState<boolean>(true); // To handle loading state
  const [yearData, setYearData] = useState<any>([]); // To handle monthly data for the chart

  // Fetch data from the backend API
  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/retail/stats"); // Adjust the API URL as necessary
      setStats(response.data.data);

      // If the monthlyData exists, process it into a chart-friendly format
      const processedMonthlyData = response.data.data.monthlyData.map(
        (item: any) => ({
          month: item.month.split(" ")[0], // Truncate month to just month name
          count: parseInt(item.count) || 0, // Default to 0 if count is missing
        })
      );

      setYearData(processedMonthlyData); // Set the processed data for the chart
      setLoading(false);
    } catch (error) {
      console.error("Error fetching stats", error);
      setLoading(false);
    }
  };

  // Fetch stats when the component mounts
  useEffect(() => {
    fetchStats();
  }, []);

  // Handlers & charts initial data
  // Placeholder data for charts in case of loading
  const defaultMonthlyApplications = [
    { forms: 0, month: "Jan" },
    { forms: 0, month: "Feb" },
    { forms: 0, month: "Mar" },
    { forms: 0, month: "Apr" },
    { forms: 0, month: "May" },
    { forms: 0, month: "Jun" },
    { forms: 0, month: "Jul" },
    { forms: 0, month: "Aug" },
    { forms: 0, month: "Sep" },
    { forms: 0, month: "Oct" },
    { forms: 0, month: "Nov" },
    { forms: 0, month: "Dec" },
  ];

  const chartSetting = {
    yAxis: [
      {
        label: "Number of Applications",
      },
    ],
    height: 250,
  };

  // Value formatter to display "Forms" label
  function valueFormatter(value: number | null) {
    return `${value} Forms`;
  }

  return (
    <Box mt={4} mx={4} height={"85vh"}>
      <Card
        sx={{
          p: 1,
          margin: "auto",
          width: "100%",
          backgroundColor: "#343434",
          color: "white",
          textAlign: "left",
        }}
      >
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 120 }}
          className="text-xl font-medium"
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              fontSize: "2rem",
              textShadow: "1px 1px 2px rgba(0, 0, 0, 0.15)",
            }}
          >
            Overview Dashboard
          </Typography>
        </motion.div>
      </Card>

      <Box sx={{ marginTop: 3 }}>
        <Grid container spacing={3}>
          {/* Cards with Skeleton Loader */}
          <Grid item xs={4}>
            <Paper
              elevation={3}
              sx={{ padding: 1, borderRadius: 3, position: "relative" }}
            >
              {loading ? (
                <>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width={"80%"} height={50} />
                  <Skeleton variant="text" width={"60%"} height={30} />
                </>
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <>
                    <FiberNew
                      color="primary"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                      }}
                    />
                    <Typography
                      fontWeight={600}
                      textAlign={"center"}
                      variant="h1"
                    >
                      {stats?.monthlyData.at(-1)?.count || 0}
                    </Typography>
                    <Typography
                      fontWeight={600}
                      textAlign={"center"}
                      fontSize={16}
                    >
                      Applications Received This Month
                    </Typography>
                  </>
                </motion.text>
              )}
            </Paper>
          </Grid>

          <Grid item xs={4}>
            <Paper
              elevation={3}
              sx={{ padding: 1, borderRadius: 3, position: "relative" }}
            >
              {loading ? (
                <>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width={"80%"} height={50} />
                  <Skeleton variant="text" width={"60%"} height={30} />
                </>
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <>
                    <PriorityHighRounded
                      color="error"
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    />
                    <Typography
                      fontWeight={600}
                      textAlign={"center"}
                      variant="h1"
                      color="error"
                    >
                      {stats?.pending || 0}
                    </Typography>
                    <Typography
                      textAlign={"center"}
                      fontSize={16}
                      fontWeight={600}
                    >
                      Total Applications Pending
                    </Typography>
                  </>
                </motion.text>
              )}
            </Paper>
          </Grid>

          <Grid item xs={4}>
            <Paper
              elevation={3}
              sx={{ padding: 1, borderRadius: 3, position: "relative" }}
            >
              {loading ? (
                <>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width={"80%"} height={50} />
                  <Skeleton variant="text" width={"60%"} height={30} />
                </>
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <>
                    <CheckCircleOutlineRounded
                      color="success"
                      sx={{ position: "absolute", top: 8, right: 8 }}
                    />
                    <Typography
                      fontWeight={600}
                      textAlign={"center"}
                      variant="h1"
                      color="success"
                    >
                      {stats?.done || 0}
                    </Typography>
                    <Typography
                      textAlign={"center"}
                      fontSize={16}
                      fontWeight={600}
                    >
                      Total Applications Marked Done
                    </Typography>
                  </>
                </motion.text>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ marginTop: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Grid sx={{ backgroundColor: "white", p: 1, borderRadius: 3 }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={250} />
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <BarChart
                    dataset={
                      yearData.length ? yearData : defaultMonthlyApplications
                    } // Use default data if yearData is empty
                    xAxis={[{ scaleType: "band", dataKey: "month" }]}
                    series={[
                      {
                        dataKey: "count",
                        label: "Applications Received",
                        valueFormatter,
                      },
                    ]}
                    layout="vertical"
                    grid={{ horizontal: true }}
                    {...chartSetting}
                  />
                </motion.text>
              )}
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Paper elevation={3} sx={{ padding: 2, borderRadius: 3 }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={200} />
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <PieChartWithCustomizedLabel
                    data={[
                      {
                        label: "Applications Pending",
                        value: stats?.pending,
                        color: "#0088FE",
                      },
                      {
                        label: "Applications Marked Done",
                        value: stats?.done,
                        color: "#00C49F",
                      },
                    ]}
                  />
                </motion.text>
              )}
              <Typography fontWeight={600} textAlign={"center"} gutterBottom>
                Applications Overview
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={3}>
            <Paper elevation={3} sx={{ padding: 2, borderRadius: 3 }}>
              {loading ? (
                <Skeleton variant="rectangular" width="100%" height={200} />
              ) : (
                <motion.text
                  initial={{ filter: "blur(20px)", opacity: 0 }}
                  animate={{ filter: "blur(0px)", opacity: 1 }}
                  transition={{ duration: 0.8 }}
                >
                  <PieChartWithCustomizedLabel
                    data={[
                      {
                        label: "Active Users",
                        value: stats?.activeUsers,
                        color: "#00C49F",
                      },
                      {
                        label: "Deactivated Users",
                        value: stats?.inactiveUsers,
                        color: "#0088FE",
                      },
                    ]}
                  />
                </motion.text>
              )}
              <Typography fontWeight={600} textAlign={"center"} gutterBottom>
                Users Overview
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OverviewPage;
